// Renders CV data in a specific template layout + accent color.
// All rendering is CSS-only, no external dependencies.
import { findTemplate, LAYOUTS } from "../lib/templates";

const monthYear = (m, y) => [m, y].filter(Boolean).join(" ");

function ContactLine({ p, className = "" }) {
  const bits = [p.email, p.phone, p.location, p.linkedin, p.website].filter(Boolean);
  if (!bits.length) return null;
  return <div className={`text-[10.5px] text-slate-500 ${className}`}>{bits.join(" · ")}</div>;
}

function Section({ title, accent, children, condensed = false, uppercase = true }) {
  const label = uppercase ? title.toUpperCase() : title;
  return (
    <div className={condensed ? "mt-3" : "mt-4"}>
      <div
        className="text-[10px] font-black tracking-[0.18em] pb-1 mb-1.5 border-b"
        style={{ color: accent, borderColor: `${accent}33` }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ExperienceBlock({ items, accent, dense }) {
  return items.map((e) => (
    <div key={e.id} className={dense ? "mb-2" : "mb-3"}>
      <div className="font-bold text-[13px]" style={{ color: "#0F172A" }}>
        {e.title || "Role"}
        <span className="font-normal text-slate-500"> · {e.company || "Company"}</span>
      </div>
      <div className="text-[10.5px] text-slate-500">
        {[e.location, `${monthYear(e.start_month, e.start_year)} – ${e.current ? "Present" : monthYear(e.end_month, e.end_year)}`].filter((x) => x && x !== " – ").join(" · ")}
      </div>
      {e.achievements?.filter(Boolean).length > 0 && (
        <ul className="mt-1 pl-4 list-disc marker:text-slate-300 text-[12px] leading-[1.5]">
          {e.achievements.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      )}
    </div>
  ));
}

function EducationBlock({ items, dense }) {
  return items.map((e) => (
    <div key={e.id} className={dense ? "mb-1.5" : "mb-2"}>
      <div className="font-bold text-[12.5px]" style={{ color: "#0F172A" }}>{e.degree || "Degree"}</div>
      <div className="text-[10.5px] text-slate-500">
        {[e.institution, e.location, [e.start_year, e.end_year].filter(Boolean).join(" – ")].filter(Boolean).join(" · ")}
      </div>
      {e.grade && <div className="text-[10.5px]">Grade: {e.grade}</div>}
      {e.details && <div className="text-[11.5px] mt-0.5">{e.details}</div>}
    </div>
  ));
}

function SkillsInline({ sk }) {
  return (
    <div className="space-y-1 text-[12px]">
      {sk.technical?.length > 0 && <div><b>Technical:</b> {sk.technical.join(", ")}</div>}
      {sk.soft?.length > 0 && <div><b>Soft:</b> {sk.soft.join(", ")}</div>}
      {sk.tools?.length > 0 && <div><b>Tools:</b> {sk.tools.join(", ")}</div>}
    </div>
  );
}

function SkillsChips({ sk, accent }) {
  const all = [...(sk.technical || []), ...(sk.tools || []), ...(sk.soft || [])];
  if (all.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {all.map((s, i) => (
        <span key={i} className="rounded-full px-2 py-0.5 text-[10.5px] font-bold"
          style={{ background: `${accent}17`, color: accent }}>{s}</span>
      ))}
    </div>
  );
}

/* -------- Layout 1: Atlas — single-column with left accent bar -------- */
function Atlas({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[13px] text-slate-800 leading-[1.55]">
      <div className="flex gap-4">
        <div className="w-1.5 rounded-full" style={{ background: accent }} />
        <div className="flex-1 min-w-0">
          <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-black text-[28px] tracking-tight">{p.full_name || "Your name"}</div>
          {p.headline && <div style={{ color: accent }} className="text-[13px] font-bold mt-0.5">{p.headline}</div>}
          <ContactLine p={p} className="mt-1" />
          {s.text && <p className="text-[12.5px] mt-3 whitespace-pre-wrap">{s.text}</p>}
        </div>
      </div>
      {(data.experience?.length > 0) && <Section title="Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
      {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} /></Section>}
      <SkillsAndLangs data={data} accent={accent} />
      {(data.projects?.length > 0) && <Projects items={data.projects} accent={accent} />}
      {(data.additional || []).map((a) => (
        <Section key={a.id} title={a.title || a.type} accent={accent}>
          <div className="whitespace-pre-wrap text-[12px]">{a.content}</div>
        </Section>
      ))}
      {watermark && <div className="mt-5 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 2: Meridian — 2-column sidebar + main -------- */
function Meridian({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {}, sk = data.skills || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[12.5px] text-slate-800 leading-[1.5] grid grid-cols-[36%_1fr] gap-5">
      <aside className="rounded-2xl p-4" style={{ background: `${accent}0f` }}>
        <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-black text-[22px] leading-tight tracking-tight">{p.full_name || "Your name"}</div>
        {p.headline && <div style={{ color: accent }} className="text-[12px] font-bold mt-0.5">{p.headline}</div>}
        <div className="space-y-1 text-[11px] text-slate-600 mt-3">
          {p.email && <div>✉ {p.email}</div>}
          {p.phone && <div>☎ {p.phone}</div>}
          {p.location && <div>◎ {p.location}</div>}
          {p.linkedin && <div>in {p.linkedin}</div>}
          {p.website && <div>↗ {p.website}</div>}
        </div>
        {(sk.technical?.length || sk.soft?.length || sk.tools?.length) > 0 && (
          <Section title="Skills" accent={accent} condensed><SkillsChips sk={sk} accent={accent} /></Section>
        )}
        {(data.languages?.length > 0) && (
          <Section title="Languages" accent={accent} condensed>
            <div className="text-[11.5px]">
              {data.languages.map((l, i) => (
                <div key={i}>{l.name}{l.proficiency ? ` — ${l.proficiency}` : ""}</div>
              ))}
            </div>
          </Section>
        )}
        {(data.certifications?.length > 0) && (
          <Section title="Certifications" accent={accent} condensed>
            <ul className="text-[11.5px] space-y-0.5">
              {data.certifications.map((c) => <li key={c.id}>• {[c.name, c.issuer].filter(Boolean).join(" — ")}</li>)}
            </ul>
          </Section>
        )}
      </aside>
      <main>
        {s.text && <Section title="Summary" accent={accent}><p className="text-[12px] whitespace-pre-wrap">{s.text}</p></Section>}
        {(data.experience?.length > 0) && <Section title="Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
        {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} /></Section>}
        {(data.projects?.length > 0) && <Projects items={data.projects} accent={accent} />}
      </main>
      {watermark && <div className="col-span-2 mt-4 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 3: Keystone — classic serif centered, ATS -------- */
function Keystone({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[13px] text-slate-800 leading-[1.55] text-left">
      <div className="text-center pb-2 border-b-2" style={{ borderColor: accent }}>
        <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-bold text-[28px] tracking-wide uppercase">{p.full_name || "Your name"}</div>
        {p.headline && <div className="italic text-[12.5px] mt-0.5" style={{ color: accent }}>{p.headline}</div>}
        <ContactLine p={p} className="mt-1" />
      </div>
      {s.text && <Section title="Objective" accent={accent}><p className="whitespace-pre-wrap text-[12.5px]">{s.text}</p></Section>}
      {(data.experience?.length > 0) && <Section title="Professional Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
      {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} /></Section>}
      <SkillsAndLangs data={data} accent={accent} />
      {(data.additional || []).map((a) => (
        <Section key={a.id} title={a.title || a.type} accent={accent}>
          <div className="whitespace-pre-wrap text-[12px]">{a.content}</div>
        </Section>
      ))}
      {watermark && <div className="mt-5 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 4: Apex — bold header block, exec -------- */
function Apex({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[13px] text-slate-800 leading-[1.55]">
      <div className="p-4 rounded-2xl text-white" style={{ background: accent }}>
        <div style={{ fontFamily: fontPair.display }} className="font-black text-[26px] leading-tight">{p.full_name || "Your name"}</div>
        {p.headline && <div className="opacity-90 text-[12.5px] mt-0.5">{p.headline}</div>}
        <div className="opacity-80 text-[10.5px] mt-2">{[p.email, p.phone, p.location, p.linkedin, p.website].filter(Boolean).join("  ·  ")}</div>
      </div>
      {s.text && <Section title="Executive Summary" accent={accent}><p className="whitespace-pre-wrap text-[12.5px]">{s.text}</p></Section>}
      {(data.experience?.length > 0) && <Section title="Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
      {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} /></Section>}
      <SkillsAndLangs data={data} accent={accent} />
      {(data.projects?.length > 0) && <Projects items={data.projects} accent={accent} />}
      {watermark && <div className="mt-5 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 5: Canvas — mono headings, technical -------- */
function Canvas({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[13px] text-slate-800 leading-[1.55]">
      <div>
        <div style={{ fontFamily: fontPair.display, color: accent }} className="text-[11px] tracking-widest uppercase">// {p.headline || "Engineer"}</div>
        <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-black text-[28px] mt-0.5 tracking-tight">{p.full_name || "Your name"}</div>
        <ContactLine p={p} className="mt-1" />
      </div>
      {s.text && <Section title="// summary" accent={accent} uppercase={false}><p className="whitespace-pre-wrap text-[12.5px]">{s.text}</p></Section>}
      {(data.experience?.length > 0) && <Section title="// experience" accent={accent} uppercase={false}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
      {(data.projects?.length > 0) && <Section title="// projects" accent={accent} uppercase={false}><Projects items={data.projects} accent={accent} bare /></Section>}
      {(data.education?.length > 0) && <Section title="// education" accent={accent} uppercase={false}><EducationBlock items={data.education} /></Section>}
      <SkillsAndLangs data={data} accent={accent} />
      {watermark && <div className="mt-5 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 6: Prism — colored sidebar + photo -------- */
function Prism({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {}, sk = data.skills || {};
  const showPhoto = data.settings?.show_photo && p.photo_url;
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[12.5px] text-slate-800 leading-[1.5] grid grid-cols-[38%_1fr] gap-4 min-h-[520px]">
      <aside className="rounded-2xl p-4 text-white" style={{ background: accent }}>
        {showPhoto && <img src={p.photo_url} alt="" className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-4 border-white/40" />}
        <div style={{ fontFamily: fontPair.display }} className="font-black text-[22px] leading-tight tracking-tight">{p.full_name || "Your name"}</div>
        {p.headline && <div className="text-[12px] opacity-95 mt-0.5">{p.headline}</div>}
        <div className="space-y-1 text-[11px] mt-3 opacity-95">
          {p.email && <div>{p.email}</div>}
          {p.phone && <div>{p.phone}</div>}
          {p.location && <div>{p.location}</div>}
          {p.linkedin && <div>{p.linkedin}</div>}
        </div>
        {(sk.technical?.length || sk.soft?.length) > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-black tracking-[0.15em] uppercase pb-1 border-b border-white/30 mb-1.5 opacity-90">Skills</div>
            <div className="flex flex-wrap gap-1">
              {[...(sk.technical || []), ...(sk.soft || [])].map((s, i) => (
                <span key={i} className="text-[10.5px] bg-white/25 rounded-full px-2 py-0.5">{s}</span>
              ))}
            </div>
          </div>
        )}
        {(data.languages?.length > 0) && (
          <div className="mt-3">
            <div className="text-[10px] font-black tracking-[0.15em] uppercase pb-1 border-b border-white/30 mb-1 opacity-90">Languages</div>
            {data.languages.map((l, i) => <div key={i} className="text-[11px]">{l.name} — {l.proficiency}</div>)}
          </div>
        )}
      </aside>
      <main>
        {s.text && <Section title="About" accent={accent}><p className="whitespace-pre-wrap text-[12px]">{s.text}</p></Section>}
        {(data.experience?.length > 0) && <Section title="Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
        {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} /></Section>}
        {(data.projects?.length > 0) && <Projects items={data.projects} accent={accent} />}
      </main>
      {watermark && <div className="col-span-2 mt-3 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 7: Signal — top ribbon, freshers -------- */
function Signal({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[13px] text-slate-800 leading-[1.55]">
      <div className="pb-2 flex items-end justify-between gap-4 border-b-4" style={{ borderColor: accent }}>
        <div>
          <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-black text-[28px] tracking-tight leading-none">{p.full_name || "Your name"}</div>
          {p.headline && <div className="text-[12.5px] font-semibold mt-1" style={{ color: accent }}>{p.headline}</div>}
        </div>
        <div className="text-right text-[10.5px] text-slate-500 leading-tight">
          {p.email && <div>{p.email}</div>}
          {p.phone && <div>{p.phone}</div>}
          {p.location && <div>{p.location}</div>}
        </div>
      </div>
      {s.text && <Section title="About" accent={accent}><p className="whitespace-pre-wrap text-[12.5px]">{s.text}</p></Section>}
      {(data.experience?.length > 0) && <Section title="Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} dense /></Section>}
      {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} dense /></Section>}
      {(data.projects?.length > 0) && <Projects items={data.projects} accent={accent} />}
      <SkillsAndLangs data={data} accent={accent} />
      {watermark && <div className="mt-4 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 8: Heritage — formal serif, executive -------- */
function Heritage({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[12.5px] text-slate-800 leading-[1.55]">
      <div className="text-center pb-3 border-b-2" style={{ borderColor: accent }}>
        <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-bold text-[30px] tracking-[0.05em]">{p.full_name || "YOUR NAME"}</div>
        {p.headline && <div className="italic text-[13px] text-slate-700 mt-1">{p.headline}</div>}
        <ContactLine p={p} className="mt-1" />
      </div>
      {s.text && <Section title="Executive Profile" accent={accent}><p className="italic whitespace-pre-wrap text-[12.5px]">{s.text}</p></Section>}
      <div className="grid grid-cols-[1fr_36%] gap-6 mt-4">
        <div>
          {(data.experience?.length > 0) && <Section title="Career History" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
          {(data.projects?.length > 0) && <Projects items={data.projects} accent={accent} />}
        </div>
        <aside>
          {(data.education?.length > 0) && <Section title="Education" accent={accent} condensed><EducationBlock items={data.education} dense /></Section>}
          <SkillsAndLangs data={data} accent={accent} sidebar />
          {(data.certifications?.length > 0) && (
            <Section title="Certifications" accent={accent} condensed>
              <ul className="text-[11.5px] space-y-0.5">
                {data.certifications.map((c) => <li key={c.id}>• {[c.name, c.issuer].filter(Boolean).join(" — ")}</li>)}
              </ul>
            </Section>
          )}
        </aside>
      </div>
      {watermark && <div className="mt-4 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 9: Pulse — compact single-page ATS -------- */
function Pulse({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[12px] text-slate-800 leading-[1.45]">
      <div className="pb-1 border-b" style={{ borderColor: accent }}>
        <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-black text-[24px] tracking-tight">{p.full_name || "Your name"}</div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          {p.headline && <div className="text-[12px] font-bold" style={{ color: accent }}>{p.headline}</div>}
          <ContactLine p={p} />
        </div>
      </div>
      {s.text && <Section title="Summary" accent={accent} condensed><p className="whitespace-pre-wrap text-[12px]">{s.text}</p></Section>}
      {(data.experience?.length > 0) && <Section title="Experience" accent={accent} condensed><ExperienceBlock items={data.experience} accent={accent} dense /></Section>}
      {(data.education?.length > 0) && <Section title="Education" accent={accent} condensed><EducationBlock items={data.education} dense /></Section>}
      <SkillsAndLangs data={data} accent={accent} />
      {watermark && <div className="mt-3 pt-1 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Layout 10: Orbit — elegant centered w/ photo, GCC -------- */
function Orbit({ data, accent, fontPair, watermark }) {
  const p = data.personal || {}, s = data.summary || {};
  const showPhoto = data.settings?.show_photo && p.photo_url;
  return (
    <div style={{ fontFamily: fontPair.body }} className="text-[13px] text-slate-800 leading-[1.55] text-center">
      {showPhoto && <img src={p.photo_url} alt="" className="w-24 h-24 rounded-full object-cover mx-auto border-4" style={{ borderColor: `${accent}55` }} />}
      <div style={{ fontFamily: fontPair.display, color: "#0F172A" }} className="font-bold text-[30px] tracking-[0.06em] mt-2 uppercase">{p.full_name || "Your name"}</div>
      {p.headline && <div className="text-[13px] mt-1" style={{ color: accent }}>{p.headline}</div>}
      <div className="text-[10.5px] text-slate-500 mt-1">{[p.email, p.phone, p.location].filter(Boolean).join(" · ")}</div>
      <div className="mx-auto w-16 h-0.5 mt-3" style={{ background: accent }} />
      <div className="text-left mt-4">
        {s.text && <Section title="Profile" accent={accent}><p className="whitespace-pre-wrap text-[12.5px] italic">{s.text}</p></Section>}
        {(data.experience?.length > 0) && <Section title="Experience" accent={accent}><ExperienceBlock items={data.experience} accent={accent} /></Section>}
        {(data.education?.length > 0) && <Section title="Education" accent={accent}><EducationBlock items={data.education} /></Section>}
        <SkillsAndLangs data={data} accent={accent} />
      </div>
      {watermark && <div className="mt-5 pt-2 border-t border-slate-100 text-center text-[9.5px] text-slate-400 italic">Created with Cavilow — cavilow.com</div>}
    </div>
  );
}

/* -------- Shared subsections -------- */
function SkillsAndLangs({ data, accent, sidebar }) {
  const sk = data.skills || {};
  const hasSkills = (sk.technical?.length || sk.soft?.length || sk.tools?.length);
  const hasLangs = (data.languages?.length > 0);
  const hasCerts = (data.certifications?.length > 0);
  if (!hasSkills && !hasLangs && !hasCerts) return null;
  return (
    <>
      {hasSkills && (
        <Section title="Skills" accent={accent} condensed={sidebar}>
          <SkillsInline sk={sk} />
        </Section>
      )}
      {hasLangs && !sidebar && (
        <Section title="Languages" accent={accent} condensed>
          <div className="text-[12px]">{data.languages.map((l) => `${l.name}${l.proficiency ? ` (${l.proficiency})` : ""}`).join(", ")}</div>
        </Section>
      )}
      {hasCerts && !sidebar && (
        <Section title="Certifications" accent={accent} condensed>
          <ul className="text-[12px] pl-4 list-disc marker:text-slate-300">
            {data.certifications.map((c) => <li key={c.id}>{[c.name, c.issuer, c.issue_date].filter(Boolean).join(" · ")}</li>)}
          </ul>
        </Section>
      )}
    </>
  );
}

function Projects({ items, accent, bare }) {
  return (
    <Section title="Projects" accent={accent}>
      {items.map((p) => (
        <div key={p.id} className="mb-2">
          <div className="font-bold text-[12.5px]" style={{ color: bare ? accent : "#0F172A" }}>{p.title || "Project"}</div>
          {p.role && <div className="text-[11px] text-slate-500">{p.role}</div>}
          {p.description && <div className="text-[12px]">{p.description}</div>}
          {p.tools && <div className="text-[10.5px] text-slate-500">Tools: {p.tools}</div>}
        </div>
      ))}
    </Section>
  );
}

const LAYOUT_MAP = {
  [LAYOUTS.ATLAS]: Atlas,
  [LAYOUTS.MERIDIAN]: Meridian,
  [LAYOUTS.KEYSTONE]: Keystone,
  [LAYOUTS.APEX]: Apex,
  [LAYOUTS.CANVAS]: Canvas,
  [LAYOUTS.PRISM]: Prism,
  [LAYOUTS.SIGNAL]: Signal,
  [LAYOUTS.HERITAGE]: Heritage,
  [LAYOUTS.PULSE]: Pulse,
  [LAYOUTS.ORBIT]: Orbit,
};

export default function TemplateRenderer({ templateId, data, watermark = false, className = "" }) {
  const tpl = findTemplate(templateId);
  const Comp = LAYOUT_MAP[tpl.layout] || Atlas;
  return (
    <div className={`${className}`} data-testid="template-renderer" data-template-id={tpl.id}>
      <Comp data={data || {}} accent={tpl.accent} fontPair={tpl.font_pair} watermark={watermark} />
    </div>
  );
}

/* Small thumbnail (used in the template picker grid) */
export function TemplateThumb({ template, sampleData }) {
  return (
    <div className="pointer-events-none scale-[0.42] origin-top-left w-[240%] h-[240%]">
      <TemplateRenderer templateId={template.id} data={sampleData} />
    </div>
  );
}

/* Sample data so thumbnails look populated */
export const SAMPLE_DATA = {
  personal: { full_name: "Alex Morgan", headline: "Senior Product Manager", email: "alex@cavilow.com", phone: "+971 50 000 0000", location: "Dubai, UAE", linkedin: "linkedin.com/in/alexm" },
  summary: { text: "Product manager with 8 years leading cross-functional teams to ship user-loved SaaS. Focused on activation, retention, and clean design systems." },
  experience: [
    { id: "e1", title: "Senior PM", company: "Acme Corp", location: "Dubai", start_month: "Jan", start_year: "2022", current: true, achievements: ["Led onboarding redesign that lifted activation 22%", "Shipped mobile app to 50k+ users", "Managed team of 6 across 3 time zones"] },
    { id: "e2", title: "Product Manager", company: "Nova Labs", location: "London", start_month: "Mar", start_year: "2019", end_month: "Dec", end_year: "2021", achievements: ["Owned pricing & billing rewrite", "Grew MRR 3.2x YoY"] },
  ],
  education: [{ id: "ed1", degree: "M.Sc. Product Management", institution: "IE Business School", location: "Madrid", start_year: "2017", end_year: "2019" }],
  skills: { technical: ["Product Strategy", "SQL", "Figma", "Amplitude"], soft: ["Leadership", "Storytelling"], tools: ["Jira", "Notion", "Linear"] },
  certifications: [{ id: "c1", name: "PMP", issuer: "PMI", issue_date: "2023" }],
  languages: [{ id: "l1", name: "English", proficiency: "Native" }, { id: "l2", name: "Arabic", proficiency: "Intermediate" }],
  projects: [{ id: "p1", title: "Cavilow Launch", role: "Product Lead", description: "Shipped resume builder from 0 to 10k users in 6 weeks.", tools: "Figma · Notion" }],
  additional: [],
  settings: { show_photo: false },
};
